import { parseISO } from "date-fns";
import { z } from "zod";

const PaymentMethodSchema = z.object({
  id: z.string().regex(/^pm_[A-Za-z0-9]+$/),
  brand: z.string(),
  last4: z.string().regex(/^\d{4}$/),
  expMonth: z.number().int().min(1).max(12),
  expYear: z.number().int(),
});
const PaymentMethodDtoSchema = PaymentMethodSchema.omit({ expMonth: true, expYear: true }).extend({
  exp_month: PaymentMethodSchema.shape.expMonth,
  exp_year: PaymentMethodSchema.shape.expYear,
});
const PaymentMethodsDtoSchema = z.object({
  environment: z.enum(["test", "live"]),
  cards: z.array(PaymentMethodDtoSchema),
});
const SetupSessionDtoSchema = z.object({
  id: z.string().uuid(),
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  expires_at: z.iso.datetime({ offset: true }),
  url: z.string().url(),
});
const SetupDetailsDtoSchema = z.object({
  business_name: z.string(),
  customer_name: z.string(),
  environment: z.enum(["test", "live"]),
  consent_version: z.string(),
  consent_text: z.string(),
  expires_at: z.iso.datetime({ offset: true }),
  publishable_key: z.string().regex(/^pk_(test|live)_/),
  stripe_account_id: z.string().regex(/^acct_[A-Za-z0-9]+$/),
});
const SetupConsentDtoSchema = z.object({
  client_secret: z.string().regex(/^seti_[A-Za-z0-9]+_secret_[A-Za-z0-9]+$/),
  publishable_key: SetupDetailsDtoSchema.shape.publishable_key,
  stripe_account_id: SetupDetailsDtoSchema.shape.stripe_account_id,
});
const SetupStatusDtoSchema = z.object({
  status: z.enum(["pending", "succeeded", "canceled"]),
  card: PaymentMethodDtoSchema.optional(),
});

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type PaymentMethodDto = z.infer<typeof PaymentMethodDtoSchema>;
export type PaymentMethodsDto = z.infer<typeof PaymentMethodsDtoSchema>;
export type SetupSessionDto = z.infer<typeof SetupSessionDtoSchema>;
export type SetupDetailsDto = z.infer<typeof SetupDetailsDtoSchema>;
export type SetupConsentDto = z.infer<typeof SetupConsentDtoSchema>;
export type SetupStatusDto = z.infer<typeof SetupStatusDtoSchema>;

export function fromPaymentMethodDtoToPaymentMethod(dto: PaymentMethodDto): PaymentMethod {
  const data = PaymentMethodDtoSchema.parse(dto);
  return { id: data.id, brand: data.brand, last4: data.last4, expMonth: data.exp_month, expYear: data.exp_year };
}
export function fromPaymentMethodsDtoToPaymentMethods(dto: PaymentMethodsDto) {
  const data = PaymentMethodsDtoSchema.parse(dto);
  return { environment: data.environment, cards: data.cards.map(fromPaymentMethodDtoToPaymentMethod) };
}
export function fromSetupSessionDtoToSetupSession(dto: SetupSessionDto) {
  const data = SetupSessionDtoSchema.parse(dto);
  const url = new URL(data.url);
  if (url.origin !== window.location.origin || url.username || url.password || url.pathname !== "/payment-methods/setup" ||
      url.search || url.hash !== `#token=${data.token}`) {
    throw new Error("The card setup link is invalid. Please try again.");
  }
  return { id: data.id, token: data.token, expiresAt: parseISO(data.expires_at), url: url.href };
}
export function fromSetupDetailsDtoToSetupDetails(dto: SetupDetailsDto) {
  const data = SetupDetailsDtoSchema.parse(dto);
  if (!data.publishable_key.startsWith(`pk_${data.environment}_`)) {
    throw new Error("Card setup is not configured for the correct Stripe environment.");
  }
  return {
    businessName: data.business_name, customerName: data.customer_name, environment: data.environment,
    consentVersion: data.consent_version, consentText: data.consent_text, expiresAt: parseISO(data.expires_at),
    publishableKey: data.publishable_key, stripeAccountId: data.stripe_account_id,
  };
}
export function fromSetupConsentDtoToSetupConsent(dto: SetupConsentDto) {
  const data = SetupConsentDtoSchema.parse(dto);
  return { clientSecret: data.client_secret, publishableKey: data.publishable_key, stripeAccountId: data.stripe_account_id };
}
export function fromSetupStatusDtoToSetupStatus(dto: SetupStatusDto) {
  const data = SetupStatusDtoSchema.parse(dto);
  return { status: data.status, card: data.card ? fromPaymentMethodDtoToPaymentMethod(data.card) : undefined };
}
export type SetupSession = ReturnType<typeof fromSetupSessionDtoToSetupSession>;
export type SetupDetails = ReturnType<typeof fromSetupDetailsDtoToSetupDetails>;
export type SetupConsent = ReturnType<typeof fromSetupConsentDtoToSetupConsent>;
export type SetupStatus = ReturnType<typeof fromSetupStatusDtoToSetupStatus>;
